import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Table from 'components/Table'
import StickyPagination from 'components/StickyPagination'

import AdvancedSearch from 'pages/Customers/AdvancedSearch'
import CustomerList from 'pages/shared/CustomerList'
import CustomerSearch from 'pages/Customers/CustomerSearch'
import DeviceLinks from 'pages/Customers/DeviceLinks'
import RemoteConnections from 'pages/shared/RemoteConnections'
import SelectedFilterList from 'pages/Customers/CustomerSearch/SelectedFilterList'

import usePagedCustomers from 'hooks/usePagedCustomers'
import useSearchParams from 'hooks/useSearchParams'

import GA4 from 'helpers/ga4'

import { PageHeader, PageTitle, SinglePanePage, screenSize } from 'styles/shared'

const ASC_SORT = 'asc'

const Customers = () => {
  const queryFromUrl = useSearchParams('q')
  const viewAllFromUrl = useSearchParams('view')
  const filtersFromUrl = useSearchParams('filters')
  const pageFromUrl = parseInt(useSearchParams('page')) || 1
  const sortAttributeFromUrl = useSearchParams('sortAttribute') || 'customer'
  const sortDirectionFromUrl = useSearchParams('sortDirection') || ASC_SORT
  const recentlyAddedCustomerIdFromUrl = useSearchParams('recentlyAddedCustomerId')

  const tagRef = useRef(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!tagRef.current) return

    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width)
    })

    observer.observe(tagRef.current)

    return () => observer.disconnect()
  }, [])

  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())

  const [customerCount, setCustomerCount] = useState(null)

  const { customersMetadata, arePagedCustomersLoaded } = usePagedCustomers({
    query: queryFromUrl,
    filters: filtersFromUrl,
    page: pageFromUrl,
    sortAttribute: sortAttributeFromUrl,
    sortDirection: sortDirectionFromUrl,
    size: '10',
    refreshTimestamp
  })

  useEffect(() => {
    if (arePagedCustomersLoaded) {
      setCustomerCount(customersMetadata?.filteredItemCount)
    }
  }, [arePagedCustomersLoaded, customersMetadata?.filteredItemCount, viewAllFromUrl])

  useEffect(() => {
    setRefreshTimestamp(Date.now())
  }, [recentlyAddedCustomerIdFromUrl])

  const navigate = useNavigate()

  const constructUrl = ({ page = pageFromUrl, sortAttribute = sortAttributeFromUrl, sortDirection = sortDirectionFromUrl, query = queryFromUrl, filters = filtersFromUrl, viewAll = viewAllFromUrl }) => {
    if (query) {
      query = query
        .split(/[,\s]+/)
        .filter(Boolean)
        .join(',')
    }
    return `/customers?page=${page}&sortAttribute=${sortAttribute}&sortDirection=${sortDirection}${viewAll ? '&view=all' : ''}${query ? `&q=${query}` : ''}${filters ? `&filters=${filters}` : ''}`
  }

  const handlePageChange = (p: number) => {
    navigate(constructUrl({ page: p }))
  }

  const selectedFilters = filtersFromUrl ? filtersFromUrl.split(',').filter(Boolean) : []

  const clearAllFiltersCallback = () => navigate(constructUrl({ viewAll: 'view=all', filters: '' }))

  const removeFilterCallback = (filter: string) => {
    const updated = selectedFilters.filter(f => f !== filter)
    navigate(constructUrl({ filters: updated.join(',') }))
  }

  return (
    <>
      {(viewAllFromUrl === '' && queryFromUrl === '' && filtersFromUrl === '')
        ? <AdvancedSearch
            onSearch={(newQuery, newFilters) => { navigate(constructUrl({ query: newQuery, filters: newFilters })) }}
          />
        : (
          <S.Container>
            <S.PageHeader>
              <PageTitle>{customerCount} Customer{customerCount === 1 ? '' : 's'}</PageTitle>
              <S.PageSubHeader>
                <S.Row ref={tagRef} $width={width}>
                  <S.CustomerSearch query={queryFromUrl} filters={filtersFromUrl} onSearch={(newQuery, newFilters) => { navigate(constructUrl({ query: newQuery, filters: newFilters })) }} showSpinner={!arePagedCustomersLoaded} />
                  <DeviceLinks refreshTimestamp={refreshTimestamp} />
                </S.Row>
                {selectedFilters.length > 0 && (
                  <SelectedFilterList
                    selectedFilters={selectedFilters}
                    clearAllFiltersCallback={clearAllFiltersCallback}
                    removeFilterCallback={removeFilterCallback}
                  />
                )}
                <RemoteConnections sendEventCallback={(action) => GA4.sendEvent({ category: 'Customer List Page', action })} />
              </S.PageSubHeader>
            </S.PageHeader>
            <S.SinglePanePage>
              <CustomerList
              updateRefreshTimestampCallback={() => setRefreshTimestamp(Date.now())}
              noDataMessage={{ title: 'No Customers', description: '' }}
              constructUrlCallback={constructUrl}
              recentlyAddedCustomerId={recentlyAddedCustomerIdFromUrl}
              />
              {customersMetadata?.filteredItemCount > 10 && (
              <StickyPagination pageCount={customersMetadata.pageCount} onPageChange={handlePageChange} page={customersMetadata.pageNumber} />
              )}
            </S.SinglePanePage>
          </S.Container>
        )}

    </>
  )
}

export default Customers

const S = {
  Container: styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 32px;
  `,
  SinglePanePage: styled(SinglePanePage)`
   @media ${screenSize.small}{
      margin-top: -15px;
    }
  `,
  PageHeader: styled(PageHeader)`
    margin-bottom: 0;
  `,
  PageSubHeader: styled.div`
    gap: 10px;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: end;
  `,
  CustomerSearch: styled(CustomerSearch)`
    display: inline-block;
    white-space: nowrap;
    flex: 1;
  `,
  Row: styled.div<{ $width: number }>`
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    flex-direction: ${({ $width }) => $width < 1420 && $width !== 0 ? 'column' : 'row'};

    @media ${screenSize.mediumOnly}{
      width: 100%;
      flex-direction: column;
      display: grid;
      gap: 10px;
      justify-content: normal;
    }
    
    @media ${screenSize.small}{
      width: 100%;
      flex-wrap: wrap;
    }
  `,
  ActionTakenCell: styled(Table.Cell)`
    flex-direction: row;
    margin-bottom: -15px;
    padding: 0;
  `
}
